import { FC } from 'react';

import { GmailIcon } from '@/icons/gmail-icon';
import { GoogleDriveIcon } from '@/icons/service-icons';
import {
  GoogleService,
  GoogleServiceStatus,
} from '@/services/google-connections/types';

export type Connector = {
  id: GoogleService;
  name: string;
  category: string;
  provider: string;
  description: string;
  Icon: FC<{ className?: string; size?: number }>;
};

export const CONNECTORS: Connector[] = [
  {
    id: 'gmail',
    name: 'Gmail',
    category: 'Google Workspace',
    provider: 'Google',
    description:
      'Lets your scheduled workflows read your inbox (e.g. funding-intake ' +
      'triage). Only you can connect or disconnect your own account.',
    Icon: GmailIcon,
  },
  {
    id: 'drive',
    name: 'Google Drive',
    category: 'Google Workspace',
    provider: 'Google',
    description:
      'Lets your workflows read and write Drive files you already have ' +
      'access to (e.g. shared-folder sync).',
    Icon: GoogleDriveIcon,
  },
];

export const FILTER_MIN = 6;
export const ALL_CATEGORIES = 'All';

export type ConnectorEntry = { connector: Connector; row: GoogleServiceStatus };

const UNLINKED_STATUSES = new Set(['not_connected', 'pending']);

export const isLinked = (row: GoogleServiceStatus) =>
  !UNLINKED_STATUSES.has(row.status);

export const needsReconnect = (row: GoogleServiceStatus) =>
  isLinked(row) && row.status !== 'connected';

export const groupConnectors = (
  services: Partial<Record<GoogleService, GoogleServiceStatus>> | undefined,
) => {
  const entries = CONNECTORS.flatMap((connector) => {
    const row = services?.[connector.id];
    return row ? [{ connector, row }] : [];
  });
  return {
    linked: entries.filter(({ row }) => isLinked(row)),
    available: entries.filter(({ row }) => !isLinked(row)),
  };
};

export const availableCategories = (available: ConnectorEntry[]) => [
  ALL_CATEGORIES,
  ...new Set(available.map(({ connector }) => connector.category)),
];

export const filterAvailable = (
  available: ConnectorEntry[],
  category: string,
  query: string,
) => {
  const q = query.trim().toLowerCase();
  return available.filter(
    ({ connector }) =>
      (category === ALL_CATEGORIES || connector.category === category) &&
      (!q || connector.name.toLowerCase().includes(q)),
  );
};
